import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { reportsApi } from '@/services/api'
import { REPORT_DEFS } from '@/components/reports/reportDefs'
import { normalizarFilas } from '@/components/reports/vocabulario'
import { nivelesDesconocidos } from '@/utils/reportBlocks'
import type { AnyReportRow, ApiResponse, ReportKey } from '@/types'

/** El backend cachea 5 min; en cliente se usa la misma ventana. */
const STALE = 5 * 60 * 1000
const GC = 10 * 60 * 1000

const FETCHERS: Record<ReportKey, () => Promise<ApiResponse<AnyReportRow[]>>> = {
  rawMaterials: reportsApi.getRawMaterials,
  fg: reportsApi.getFg,
  fgToRm: reportsApi.getFgToRm,
  consumoProduccion: reportsApi.getConsumoProduccion,
}

/**
 * Filas completas de un report, ya ordenadas por el backend.
 *
 * Se trae el conjunto entero a propósito: la unidad de filtrado es el bloque
 * (cabecera + sublíneas) y los tres reports son de cientos de filas, así que
 * filtrar en cliente sale más barato que ir al servidor por cada cambio de
 * filtro — cada consulta a la vista cuesta segundos.
 */
export function useReport(key: ReportKey) {
  const query = useQuery({
    queryKey: ['reports', key],
    queryFn: FETCHERS[key],
    staleTime: STALE,
    gcTime: GC,
  })

  // Identidad ESTABLE mientras no cambie la respuesta. Con `?? []` suelto, cada
  // render devolvía un array nuevo, y cualquier `useMemo` que dependa de las filas
  // —el catálogo de valores de los filtros, por ejemplo— se recalculaba en cada
  // render. Si de ese memo cuelga un efecto que hace setState, sale un bucle de
  // renders y la petición del report no llega ni a salir.
  //
  // Y aquí es donde se traduce el vocabulario de las vistas: en la ENTRADA, un
  // solo sitio, para que el resto de la capa no tenga que conocer dos idiomas.
  // Ver `reports/vocabulario.ts` — es temporal y se borra cuando las vistas
  // estén en inglés.
  const rows = useMemo(() => normalizarFilas(query.data?.data ?? []), [query.data])

  // Centinela de contrato: si la vista emite un `tipo_fila` que el registro no
  // conoce, la pantalla no falla — la subtabla de esas filas sale vacía y no hay
  // nada que lo explique. Se avisa una vez por respuesta, con el valor entre
  // comillas para que un recorte de VARCHAR se vea a simple vista.
  useEffect(() => {
    const def = REPORT_DEFS[key]
    const raros = nivelesDesconocidos(rows, [def.headerType, ...def.detailTypes])
    if (raros.length > 0) {
      console.warn(
        `[reports/${key}] tipo_fila que el registro no reconoce: ${raros.map(r => `"${r}"`).join(', ')}. `
        + `Declarados: ${[def.headerType, ...def.detailTypes].join(', ')}. `
        + 'Esas filas se pintan pero su subtabla saldrá vacía.',
      )
    }
  }, [rows, key])

  return {
    rows,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    // Marca de tiempo del dato, no de la petición: viene en el ApiResponse.
    updatedAt: query.data?.updatedAt,
    refetch: query.refetch,
  }
}
